import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Bookmark, Trash2, Pencil, UserPlus, MapPin, Route as RouteIcon, Search,
  Clock, Navigation, Calendar, CheckCircle2, PlayCircle, CircleDashed,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const STATUS_STYLES = {
  planned: { label: "Geplant", cls: "bg-muted text-muted-foreground", Icon: CircleDashed },
  active:  { label: "Aktiv",   cls: "bg-primary/15 text-primary",      Icon: PlayCircle },
  done:    { label: "Erledigt",cls: "bg-accent/15 text-accent",        Icon: CheckCircle2 },
};

export default function Tours() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [tours, setTours] = useState([]);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [t, u] = await Promise.all([
        api.get("/tours"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setTours(t.data || []);
      setUsers(u.data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map(u => [u.id, u])), [users]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tours.filter(t => {
      if (statusFilter !== "all" && (t.status || "planned") !== statusFilter) return false;
      if (!q) return true;
      return (
        (t.name || "").toLowerCase().includes(q) ||
        (t.notes || "").toLowerCase().includes(q) ||
        (userMap[t.assigned_to]?.name || "").toLowerCase().includes(q)
      );
    });
  }, [tours, query, statusFilter, userMap]);

  const stats = useMemo(() => ({
    total: tours.length,
    planned: tours.filter(t => (t.status || "planned") === "planned").length,
    active: tours.filter(t => t.status === "active").length,
    done: tours.filter(t => t.status === "done").length,
    km: tours.reduce((s, t) => s + (Number(t.total_km) || 0), 0),
    stops: tours.reduce((s, t) => s + ((t.stops || []).length), 0),
  }), [tours]);

  function openEdit(t) {
    setEditing({
      id: t.id,
      name: t.name || "",
      date: t.date || "",
      assigned_to: t.assigned_to || "",
      status: t.status || "planned",
      notes: t.notes || "",
    });
    setEditOpen(true);
  }

  async function saveEdit() {
    if (!editing?.name.trim()) { toast.error("Name erforderlich"); return; }
    try {
      await api.patch(`/tours/${editing.id}`, {
        name: editing.name.trim(),
        date: editing.date || null,
        assigned_to: editing.assigned_to || null,
        status: editing.status,
        notes: editing.notes,
      });
      toast.success("Tour aktualisiert");
      setEditOpen(false);
      load();
    } catch { toast.error("Fehler beim Speichern"); }
  }

  async function quickStatus(t, status) {
    try {
      await api.patch(`/tours/${t.id}`, { status });
      toast.success(`Status: ${STATUS_STYLES[status].label}`);
      load();
    } catch { toast.error("Fehler"); }
  }

  async function quickAssign(t, assigned_to) {
    try {
      await api.patch(`/tours/${t.id}`, { assigned_to: assigned_to || null });
      toast.success("Zuweisung aktualisiert");
      load();
    } catch { toast.error("Fehler"); }
  }

  async function removeTour(t) {
    if (!window.confirm(`Tour "${t.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/tours/${t.id}`);
      toast.success("Tour gelöscht");
      load();
    } catch { toast.error("Fehler"); }
  }

  function openOnMap(t) {
    nav(`/map?tour=${t.id}`);
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="tours-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Tourenverwaltung</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Touren</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Alle gespeicherten Touren mit Stopps, KM und Zuweisungen
          </p>
        </div>
        <Button className="rounded-full" onClick={() => nav("/map")} data-testid="btn-new-tour-on-map">
          <Navigation className="w-4 h-4 mr-2" /> Neue Tour auf Karte planen
        </Button>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Touren" value={stats.total} icon={Bookmark} />
        <StatCard label="Stopps" value={stats.stops} icon={MapPin} />
        <StatCard label="Gesamt-KM" value={stats.km.toFixed(0)} icon={RouteIcon} accent />
        <StatCard label="Aktiv" value={stats.active} icon={PlayCircle} />
        <StatCard label="Erledigt" value={stats.done} icon={CheckCircle2} />
      </div>

      {/* Filter bar */}
      <Card className="rounded-3xl p-4 border-border/60 glass flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Name, Notiz, Techniker suchen..."
            className="rounded-full h-10 pl-9"
            data-testid="tours-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-full h-10 w-44" data-testid="tours-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="planned">Geplant</SelectItem>
            <SelectItem value="active">Aktiv</SelectItem>
            <SelectItem value="done">Erledigt</SelectItem>
          </SelectContent>
        </Select>
      </Card>

      {/* Tours list */}
      {loading ? (
        <div className="py-20 flex items-center justify-center">
          <div className="w-8 h-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-3xl p-14 text-center border-dashed border-border/60" data-testid="tours-empty">
          <div className="w-16 h-16 rounded-3xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
            <Bookmark className="w-8 h-8" />
          </div>
          <h3 className="font-heading text-xl font-semibold mb-1">
            {tours.length === 0 ? "Noch keine Touren" : "Keine Treffer"}
          </h3>
          <p className="text-sm text-muted-foreground mb-5">
            {tours.length === 0
              ? "Importiere Adressen oder plane eine Tour auf der Karte."
              : "Filter anpassen oder Suche zurücksetzen."}
          </p>
          <Button className="rounded-full" onClick={() => nav("/map")}>
            <Navigation className="w-4 h-4 mr-2" /> Zur Karte
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(t => {
            const st = STATUS_STYLES[t.status || "planned"];
            const assignee = userMap[t.assigned_to];
            return (
              <Card key={t.id}
                    className="rounded-3xl p-5 border-border/60 glass hover:border-primary/50 transition group flex flex-col"
                    data-testid={`tour-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge className={`rounded-full text-[10px] ${st.cls}`}>
                        <st.Icon className="w-3 h-3 mr-1" /> {st.label}
                      </Badge>
                      {t.source === "import" && (
                        <Badge variant="secondary" className="rounded-full text-[10px]">Import</Badge>
                      )}
                    </div>
                    <h3 className="font-heading text-lg font-semibold truncate" title={t.name}>
                      {t.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition">
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full"
                            onClick={() => openEdit(t)} title="Bearbeiten"
                            data-testid={`tour-edit-${t.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon"
                            className="h-8 w-8 rounded-full text-destructive hover:bg-destructive/10"
                            onClick={() => removeTour(t)} title="Löschen"
                            data-testid={`tour-delete-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  <Metric label="Stopps" value={(t.stops || []).length} />
                  <Metric label="km" value={Number(t.total_km || 0).toFixed(0)} highlight />
                  <Metric label="Min" value={Math.round(t.total_minutes || 0)} />
                </div>

                <div className="space-y-2 text-xs text-muted-foreground mb-4">
                  {t.date && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(t.date).toLocaleDateString("de-DE", {
                        weekday: "short", day: "2-digit", month: "short", year: "numeric",
                      })}
                    </div>
                  )}
                  {t.created_at && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" />
                      Erstellt {new Date(t.created_at).toLocaleDateString("de-DE")}
                    </div>
                  )}
                  {user?.role === "admin" ? (
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5" />
                      <Select
                        value={t.assigned_to || "__none__"}
                        onValueChange={v => quickAssign(t, v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger className="h-7 rounded-full text-xs px-3" data-testid={`tour-assign-${t.id}`}>
                          <SelectValue placeholder="Nicht zugewiesen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Keine Zuweisung —</SelectItem>
                          {users.filter(u => u.role !== "customer").map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : assignee ? (
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-3.5 h-3.5" />
                      {assignee.name}
                    </div>
                  ) : null}
                  {t.notes && (
                    <div className="text-[11px] italic truncate" title={t.notes}>
                      &ldquo;{t.notes}&rdquo;
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-auto">
                  <Button className="flex-1 rounded-full" size="sm"
                          onClick={() => openOnMap(t)}
                          data-testid={`tour-open-${t.id}`}>
                    <MapPin className="w-4 h-4 mr-1.5" /> Auf Karte öffnen
                  </Button>
                  {(t.status || "planned") === "planned" && (
                    <Button variant="outline" size="sm" className="rounded-full"
                            onClick={() => quickStatus(t, "active")}
                            data-testid={`tour-start-${t.id}`}>
                      Start
                    </Button>
                  )}
                  {t.status === "active" && (
                    <Button variant="outline" size="sm" className="rounded-full"
                            onClick={() => quickStatus(t, "done")}
                            data-testid={`tour-finish-${t.id}`}>
                      Fertig
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" /> Tour bearbeiten
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={editing.name}
                       onChange={e => setEditing({ ...editing, name: e.target.value })}
                       className="rounded-full h-10 px-4 mt-1" data-testid="edit-tour-name" />
              </div>
              <div>
                <Label>Datum</Label>
                <Input type="date" value={editing.date}
                       onChange={e => setEditing({ ...editing, date: e.target.value })}
                       className="rounded-full h-10 px-4 mt-1" data-testid="edit-tour-date" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status}
                        onValueChange={v => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="rounded-full h-10 px-4 mt-1" data-testid="edit-tour-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planned">Geplant</SelectItem>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="done">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zuweisen an</Label>
                <Select value={editing.assigned_to || "__none__"}
                        onValueChange={v => setEditing({ ...editing, assigned_to: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-full h-10 px-4 mt-1" data-testid="edit-tour-assign">
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Keine Zuweisung —</SelectItem>
                    {users.filter(u => u.role !== "customer").map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notizen</Label>
                <Input value={editing.notes}
                       onChange={e => setEditing({ ...editing, notes: e.target.value })}
                       className="rounded-full h-10 px-4 mt-1" data-testid="edit-tour-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditOpen(false)}>
              Abbrechen
            </Button>
            <Button className="rounded-full" onClick={saveEdit} data-testid="edit-tour-save">
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card className={`rounded-3xl p-4 border-border/60 glass flex items-center gap-3 ${accent ? "bg-accent/5" : ""}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${accent ? "bg-accent/15 text-accent" : "bg-primary/15 text-primary"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="font-heading text-2xl font-bold leading-tight">{value}</div>
        <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
      </div>
    </Card>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <div className={`rounded-2xl p-2.5 text-center ${highlight ? "bg-primary/10" : "bg-secondary/50"}`}>
      <div className={`font-heading font-bold text-lg ${highlight ? "text-primary" : ""}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
    </div>
  );
}
